import React, { useEffect, useMemo, useRef, useState } from "react";
import { Mail, Plus, Save, Send, Trash2, Copy, ToggleLeft, ToggleRight, Paperclip, UploadCloud, X } from "lucide-react";
import { api } from "../../lib/api";
import Spinner from "../../components/Spinner";

const EMPTY_TEMPLATE = {
  name: "Nouveau modèle email",
  trigger: "manual",
  subject: "Objet du nouvel email",
  body: "Bonjour {{ prenom_client }},\n\nÉcris ton message ici.",
  is_active: false,
  delay_hours: 0,
  description: "",
  attachments: [],
};

function triggerLabel(triggers, value) {
  return triggers.find((t) => t.value === value)?.label || value || "Manuel";
}

export default function AdminEmails() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [smtp, setSmtp] = useState({ configured: false });
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [error, setError] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [activeField, setActiveField] = useState("body");
  const [cursorPositions, setCursorPositions] = useState({ subject: 0, body: 0, description: 0 });
  const fieldRefs = {
    subject: useRef(null),
    body: useRef(null),
    description: useRef(null),
  };

  const selected = useMemo(
    () => templates.find((t) => t.template_id === selectedId) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      setError("");

      try {
        const { data } = await api.get("/admin/email/templates");
        const items = data.items || [];
        const firstId = items[0]?.template_id || null;
        const first = items[0] || null;

        setTemplates(items);
        setVariables(data.variables || []);
        setTriggers(data.triggers || []);
        setSmtp(data.smtp || { configured: false });
        setSelectedId(firstId);
        setDraft(first ? { ...first } : null);
      } catch (err) {
        setError(err?.response?.data?.detail || "Impossible de charger les modèles email.");
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  useEffect(() => {
    if (selected) {
      setDraft({ ...selected });
      setPreview(null);
    }
  }, [selectedId, selected]);

  const updateDraft = (key, value) => {
    setDraft((prev) => ({ ...(prev || EMPTY_TEMPLATE), [key]: value }));
  };

  const rememberCursor = (field, element) => {
    setActiveField(field);
    setCursorPositions((prev) => ({
      ...prev,
      [field]: element?.selectionStart ?? (draft?.[field] || "").length,
    }));
  };

  const focusFieldAt = (field, position) => {
    window.setTimeout(() => {
      const el = fieldRefs[field]?.current;
      if (!el) return;
      el.focus();
      if (typeof el.setSelectionRange === "function") {
        el.setSelectionRange(position, position);
      }
    }, 0);
  };

  const createTemplate = async () => {
    setSaving(true);
    setError("");

    try {
      const payload = {
        ...EMPTY_TEMPLATE,
        subject: EMPTY_TEMPLATE.subject.trim(),
        body: EMPTY_TEMPLATE.body.trim(),
      };

      const { data } = await api.post("/admin/email/templates", payload);
      setTemplates((prev) => [...prev, data]);
      setSelectedId(data.template_id);
      setDraft({ ...data });
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de créer le modèle email.");
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    if (!draft?.template_id) return;

    const subject = String(draft.subject || "").trim();
    const body = String(draft.body || "").trim();

    if (!subject) {
      setError("Objet requis : ajoute un objet avant de sauvegarder ce modèle.");
      return;
    }

    if (!body) {
      setError("Corps du mail requis : ajoute un contenu avant de sauvegarder ce modèle.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        name: draft.name,
        trigger: draft.trigger,
        subject,
        body,
        is_active: !!draft.is_active,
        delay_hours: Number(draft.delay_hours || 0),
        description: draft.description || "",
        attachments: Array.isArray(draft.attachments) ? draft.attachments : [],
      };

      const { data } = await api.patch(`/admin/email/templates/${draft.template_id}`, payload);

      setTemplates((prev) =>
        prev.map((t) => (t.template_id === data.template_id ? data : t))
      );

      setDraft({ ...data });
      alert("Modèle email sauvegardé.");
      return data;
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de sauvegarder le modèle email.");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async () => {
    if (!draft?.template_id) return;

    const ok = window.confirm(`Supprimer le modèle “${draft.name}” ?`);
    if (!ok) return;

    setSaving(true);
    setError("");

    try {
      await api.delete(`/admin/email/templates/${draft.template_id}`);

      const remaining = templates.filter((t) => t.template_id !== draft.template_id);
      setTemplates(remaining);
      setSelectedId(remaining[0]?.template_id || null);
      setDraft(remaining[0] ? { ...remaining[0] } : null);
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de supprimer le modèle email.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!draft?.template_id) return;

    setTesting(true);
    setError("");

    try {
      await api.post(`/admin/email/templates/${draft.template_id}/test`, {
        email: testEmail || undefined,
      });

      alert(testEmail ? `Email de test envoyé à ${testEmail}.` : "Email de test envoyé à ton adresse admin.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’envoyer le test email.");
    } finally {
      setTesting(false);
    }
  };

  const loadPreview = async () => {
    if (!draft?.template_id) return;

    try {
      await saveTemplate();
      const { data } = await api.post(`/admin/email/templates/${draft.template_id}/preview`, {});
      setPreview(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de générer l’aperçu.");
    }
  };


  const readPdfAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        resolve(result.includes(",") ? result.split(",").pop() : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadEmailAttachment = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Seuls les fichiers PDF peuvent être ajoutés aux emails automatiques.");
      return;
    }

    setUploadingAttachment(true);
    setError("");

    try {
      const contentBase64 = await readPdfAsBase64(file);

      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const nextAttachment = {
        url: data.url,
        filename: data.filename || file.name,
        content_type: data.content_type || "application/pdf",
        content_base64: contentBase64,
      };

      setDraft((prev) => ({
        ...(prev || EMPTY_TEMPLATE),
        attachments: [...(prev?.attachments || []), nextAttachment].slice(0, 5),
      }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’ajouter le PDF au modèle email.");
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeEmailAttachment = (indexToRemove) => {
    setDraft((prev) => ({
      ...(prev || EMPTY_TEMPLATE),
      attachments: (prev?.attachments || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const insertVariable = (key, preferredField = null) => {
    const token = `{{ ${key} }}`;
    const target = preferredField || activeField || "body";
    const currentValue = draft?.[target] || "";
    const cursor = Math.min(
      cursorPositions[target] ?? currentValue.length,
      currentValue.length
    );

    const before = currentValue.slice(0, cursor);
    const after = currentValue.slice(cursor);
    const nextValue = `${before}${token}${after}`;
    const nextCursor = cursor + token.length;

    setDraft((prev) => ({
      ...(prev || EMPTY_TEMPLATE),
      [target]: nextValue,
    }));
    setCursorPositions((prev) => ({ ...prev, [target]: nextCursor }));
    setActiveField(target);
    navigator.clipboard?.writeText(token).catch(() => {});
    focusFieldAt(target, nextCursor);
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-emails-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-4xl font-black">Emails automatiques</h1>
          <p className="text-gray-600 mt-2">
            Crée, personnalise et teste les emails envoyés aux apprenants.
          </p>
        </div>

        <button
          type="button"
          onClick={createTemplate}
          disabled={saving}
          className="btn-primary self-start md:self-auto"
        >
          <Plus className="h-4 w-4" /> Ajouter un modèle
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!smtp?.configured && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          SMTP non configuré : tu peux préparer tes modèles, mais l’envoi réel nécessite les Secrets SMTP dans Emergent.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <aside className="card p-4 h-fit">
          <p className="label mb-3">Modèles</p>

          <div className="space-y-2 max-h-[620px] overflow-y-auto pr-1">
            {templates.map((template) => (
              <button
                key={template.template_id}
                type="button"
                onClick={() => setSelectedId(template.template_id)}
                className={`w-full rounded-xl px-3 py-3 text-left transition ${
                  selectedId === template.template_id
                    ? "bg-emerald-50 text-brand-green"
                    : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{template.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {triggerLabel(triggers, template.trigger)}
                    </p>
                  </div>

                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      template.is_active ? "bg-brand-green" : "bg-gray-300"
                    }`}
                  />
                </div>
              </button>
            ))}
          </div>

          <div className="border-t border-emerald-100 mt-5 pt-5 text-sm text-gray-600">
            <p className="font-heading text-lg font-bold text-black mb-2">
              Variables disponibles
            </p>
            <p className="mb-3">
              Clique pour insérer dans le champ actif, à l’emplacement du curseur.
            </p>

            <div className="flex flex-wrap gap-2">
              {variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVariable(v.key)}
                  className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-brand-green hover:bg-emerald-50"
                  title={v.label}
                >
                  <Copy className="mr-1 inline h-3 w-3" /> {`{{ ${v.key} }}`}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="card p-5 md:p-7">
          {!draft ? (
            <div className="py-16 text-center text-gray-500">
              <Mail className="mx-auto mb-3 h-12 w-12 text-emerald-200" />
              <p>Aucun modèle email pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="label">Édition du modèle</p>
                  <h2 className="font-heading text-2xl font-black">
                    {draft.name || "Modèle email"}
                  </h2>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => updateDraft("is_active", !draft.is_active)}
                    className={`btn-ghost ${draft.is_active ? "text-brand-green" : "text-gray-500"}`}
                  >
                    {draft.is_active ? (
                      <ToggleRight className="h-5 w-5" />
                    ) : (
                      <ToggleLeft className="h-5 w-5" />
                    )}
                    {draft.is_active ? "Activé" : "Désactivé"}
                  </button>

                  <button
                    type="button"
                    onClick={deleteTemplate}
                    disabled={saving}
                    className="btn-ghost text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" /> Supprimer
                  </button>

                  <button
                    type="button"
                    onClick={saveTemplate}
                    disabled={saving}
                    className="btn-primary"
                  >
                    <Save className="h-4 w-4" /> {saving ? "Sauvegarde..." : "Mettre à jour"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label mb-1 block">Nom du modèle</label>
                  <input
                    className="input"
                    value={draft.name || ""}
                    onChange={(e) => updateDraft("name", e.target.value)}
                  />
                </div>

                <div>
                  <label className="label mb-1 block">Déclencheur</label>
                  <select
                    className="input"
                    value={draft.trigger || "manual"}
                    onChange={(e) => updateDraft("trigger", e.target.value)}
                  >
                    {triggers.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label mb-1 block">Délai avant envoi (heures)</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={draft.delay_hours || 0}
                    onChange={(e) => updateDraft("delay_hours", e.target.value)}
                  />
                </div>

                <div>
                  <label className="label mb-1 block">Description interne</label>
                  <input
                    ref={fieldRefs.description}
                    className="input"
                    value={draft.description || ""}
                    onFocus={(e) => rememberCursor("description", e.target)}
                    onClick={(e) => rememberCursor("description", e.target)}
                    onKeyUp={(e) => rememberCursor("description", e.target)}
                    onChange={(e) => {
                      updateDraft("description", e.target.value);
                      rememberCursor("description", e.target);
                    }}
                    placeholder="Ex : envoyé après achat"
                  />
                </div>
              </div>

              <div>
                <label className="label mb-1 block">Objet</label>
                <input
                  ref={fieldRefs.subject}
                  className="input"
                  value={draft.subject || ""}
                  onFocus={(e) => rememberCursor("subject", e.target)}
                  onClick={(e) => rememberCursor("subject", e.target)}
                  onKeyUp={(e) => rememberCursor("subject", e.target)}
                  onChange={(e) => {
                    updateDraft("subject", e.target.value);
                    rememberCursor("subject", e.target);
                  }}
                  placeholder="Bienvenue {{ prenom_client }}"
                />

                <div className="mt-2 flex flex-wrap gap-2">
                  {["prenom_client", "nom_formation", "nom_du_site"].map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => insertVariable(key, "subject")}
                      className="text-xs font-semibold text-brand-green hover:underline"
                    >
                      + {`{{ ${key} }}`}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label mb-1 block">Corps du mail</label>
                <textarea
                  ref={fieldRefs.body}
                  className="input min-h-[260px] font-mono text-sm leading-relaxed"
                  value={draft.body || ""}
                  onFocus={(e) => rememberCursor("body", e.target)}
                  onClick={(e) => rememberCursor("body", e.target)}
                  onKeyUp={(e) => rememberCursor("body", e.target)}
                  onChange={(e) => {
                    updateDraft("body", e.target.value);
                    rememberCursor("body", e.target);
                  }}
                  placeholder="Bonjour {{ prenom_client }}, ..."
                />
              </div>


              <div className="rounded-2xl border border-emerald-100 bg-white p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="label mb-1 flex items-center gap-2"><Paperclip className="h-4 w-4" /> Pièces jointes PDF</p>
                    <p className="text-sm text-gray-600">Ajoute jusqu’à 5 PDF qui partiront avec ce modèle email automatique.</p>
                  </div>
                  <label className={`btn-ghost cursor-pointer ${uploadingAttachment ? "opacity-60 pointer-events-none" : ""}`}>
                    <UploadCloud className="h-4 w-4" /> {uploadingAttachment ? "Upload..." : "Ajouter un PDF"}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={uploadEmailAttachment}
                      disabled={uploadingAttachment}
                    />
                  </label>
                </div>

                {(draft.attachments || []).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {(draft.attachments || []).map((attachment, index) => (
                      <div key={`${attachment.url}-${index}`} className="flex items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-sm">
                        <a href={attachment.url} target="_blank" rel="noreferrer" className="min-w-0 truncate font-semibold text-brand-green hover:underline">
                          {attachment.filename || `Document ${index + 1}.pdf`}
                        </a>
                        <button type="button" onClick={() => removeEmailAttachment(index)} className="text-red-600 hover:text-red-700" aria-label="Retirer le PDF">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div className="flex-1">
                    <label className="label mb-1 block">Email de test</label>
                    <input
                      className="input bg-white"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="vide = email admin"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button type="button" onClick={loadPreview} className="btn-ghost">
                      Aperçu
                    </button>

                    <button
                      type="button"
                      onClick={sendTest}
                      disabled={testing || !smtp?.configured}
                      className="btn-primary"
                    >
                      <Send className="h-4 w-4" /> {testing ? "Envoi..." : "Envoyer un test"}
                    </button>
                  </div>
                </div>

                {preview && (
                  <div className="mt-4 rounded-xl bg-white p-4 text-sm">
                    <p className="label mb-2">Aperçu rendu</p>
                    <p className="font-bold">Objet : {preview.subject}</p>
                    <div className="mt-3 whitespace-pre-wrap text-gray-700">
                      {preview.body}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}